import { ElementRef, Optional, Pipe, PipeTransform } from '@angular/core';
import { Store } from '@ngrx/store';
import { first } from 'rxjs';

import { appInject } from 'src/app/app-injector';
import { fromCarl } from 'src/app/store/common/common.selectors';

export const RETURN_TRANSLATION_OR_KEY = 'ReturnTranslationOrKey';

/**
 * Returns the translation or undefined (sync)
 */
export const translate = (key: string, ...params: string[]): string => {
  let translation = '';
  const hasReturnTranslationOrKeyParam = (): boolean => {
    const returnTranslationOrKeyIdx = params.indexOf(RETURN_TRANSLATION_OR_KEY);
    if (returnTranslationOrKeyIdx !== -1) {
      // remove the param from the list to avoid conflict on translation
      params.splice(returnTranslationOrKeyIdx, 1);
    }

    return returnTranslationOrKeyIdx !== -1;
  };

  const shouldReturnTranslationOrKey = hasReturnTranslationOrKeyParam();

  appInject(Store)
    .select(fromCarl.selectTranslation(key, ...params))
    .pipe(first()) // intentionally close stream
    .subscribe((translationFromStore: string) => {
      if (translationFromStore) {
        translation = translationFromStore;
      } else if (shouldReturnTranslationOrKey) {
        translation = key;
      }
    });

  return translation;
};

@Pipe({
  name: 'translate',
  pure: true, // intentionally as we dont have state
  standalone: true
})
export class TranslatePipe implements PipeTransform {
  public constructor(@Optional() private readonly elementRef?: ElementRef<HTMLElement>) {}

  public transform(key: string, ...params: string[]): string | undefined {
    /**
     * Sets the used translation key as data attribute of the current node or, if
     * the node is not an element, the parent element of the current node.
     *
     * Used for QA for selecting elements.
     *
     * @param ref - string  The translation key with its optional params
     */
    const setMarkupReference = (ref: string): void => {
      let element = this.elementRef?.nativeElement;
      if (element) {
        // if current node is no element, find the next parent element
        while (element.nodeName.startsWith('#') && element.parentElement) {
          element = element.parentElement;
        }

        element.dataset.translateKeys = `["${ref}"]`;
      }
    };

    const reference = [key, ...params].filter(Boolean).join(':');
    setMarkupReference(reference);
    return translate(key, ...params);
  }
}
